import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { startWith, takeUntil, interval } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ReferenceCache } from '../../reference/services/reference.cache';
import { PartComponent } from '../../reference/models/part-component';
import { ClaimsAppSubmissionConstant } from '../models/claims-app-submission.constant';
import { CorrectionEstimateListComponent } from '../estimates/correction-estimate-list/correction-estimate-list.component';
import { ComponentSelector } from '../component-selection';
import { ComponentGroupService } from '../services/form-service/component-group.service';

@Component({
  selector: 'concern-cause-part',
  templateUrl: './concern-cause-part.component.html',
  styleUrls: ['./concern-cause-part.component.scss']
})
export class ConcernCausePartComponent extends ComponentSelector implements OnInit, OnDestroy {
  @Input() partComponent: PartComponent;
  @Input() override isReadOnly: boolean = false;
  @ViewChild(CorrectionEstimateListComponent) correctionEstimateList: CorrectionEstimateListComponent;
  public enableEstimateUi: boolean = environment.enableEstimateUi == "true";
 
  public override form: UntypedFormGroup;

  public componentGroupOptions: string[] = [];
  public componentGroupFormControl: FormControl<string>;

  // Public properties for template binding (replacing getters)
  public isLoading: boolean = false;
  public isCustomComponent: boolean = false;
  public enableGroupClear: boolean = false;

  constructor(protected override referenceCache: ReferenceCache,
              protected override componentGroupService: ComponentGroupService,
              protected dialog: MatDialog) {
    super(referenceCache, componentGroupService);
  }

  override ngOnInit(): void {
    // Initialize with an empty group name.
    this.componentGroupKey = this.componentGroupService.add('');
    
    super.ngOnInit();
    this.allowCustomPart = true;  // Allow custom part selection.
    
    // Set component group observable to the concern group form control when value changes.
    this.componentGroupFormControl = new FormControl({ value: "", disabled: true });
    this.componentGroup = this.componentGroupFormControl.valueChanges.pipe(takeUntil(this._destroying$),
                                                                           startWith(this.componentGroupFormControl.value ?? ''));
    
    this.form = new UntypedFormGroup({
      'component': this.componentFormControl,
      'concern': new UntypedFormControl("", [Validators.required, Validators.maxLength(500)]),
      'cause': new UntypedFormControl("", [Validators.required, Validators.maxLength(500)]),
      'componentGroup': this.componentGroupFormControl
    });    
    
    this.referenceCache.componentGroupsSubject
        .pipe(takeUntil(this._destroying$))
        .subscribe(groups => {
          this.componentGroupOptions = groups;
        });

    // Initialize property values
    this.updateCachedProperties();

    // Set up reactive updates for loading state
    interval(100)
        .pipe(takeUntil(this._destroying$))
        .subscribe(() => {
          const previousLoading = this.isLoading;
          this.updateLoadingState();
        });

    // Set up reactive updates for form-dependent properties
    this.form.valueChanges
        .pipe(takeUntil(this._destroying$))
        .subscribe(() => {
          this.updateCachedProperties();
        });
  }

  /**
   * Update cached properties when dependencies change.
   */
  private updateCachedProperties(): void {
    this.updateLoadingState();
    this.updateIsCustomComponent();
    this.updateEnableGroupClear();
  }

  /**
   * Update the isLoading property.
   */
  private updateLoadingState(): void {
    this.isLoading = this.referenceCache.updatingComponents;
  }

  /**
   * Update the isCustomComponent property.
   */
  private updateIsCustomComponent(): void {
    if (!this.form?.get('component')) {
      this.isCustomComponent = false;
      return;
    }

    this.isCustomComponent = this.form.get('component').disabled
           && (this.form.getRawValue()['component']?.groupName === ClaimsAppSubmissionConstant.createNewComponentLabel);
  }

  /**
   * Update the enableGroupClear property.
   */
  private updateEnableGroupClear(): void {
    this.enableGroupClear = !this.isReadOnly
           && this.isCustomComponent
           && this.partComponent?.groupName === ClaimsAppSubmissionConstant.createNewComponentLabel;
  }



  /**
   * When child selects a part component, update the componentGroup form control.
   * And update the partComponent reference (this might need revising).
   * @param componentOptions
   */
  public onSelectedComponent(): void {
    this.componentFormControl.disable();
    
    var selectedComponent = this.componentFormControl?.value as PartComponent
    this.partComponent = new PartComponent(selectedComponent?.name,
                                           selectedComponent?.groupName,
                                           selectedComponent?.code);

    // If custom, enable group selection.
    if (this.partComponent?.groupName == ClaimsAppSubmissionConstant.createNewComponentLabel) {
      this.componentGroupFormControl.enable();
    }
    else {
      // Else set group from part component selection.
      this.componentGroupFormControl.setValue(this.partComponent.groupName);
      this.componentGroupService.set(this.componentGroupKey, this.partComponent.groupName);
    }

    // Enable component selection if nothing is selected.
    if (this.partComponent?.name == null) {
      this.componentFormControl.enable();
    }
    
    // Update cached properties after component selection changes
    this.updateCachedProperties();
  }

  /**
   * When component group is selected. Set the component group.
   * Should only be called for custom components.
   */
  public onSelectComponentGroup(): void {
    this.partComponent.groupName = this.componentGroupFormControl?.value;

    this.setComponentGroup(this.partComponent.groupName);
    this.componentGroupFormControl.disable();
    
    // Update cached properties after component group changes
    this.updateCachedProperties();
  }

  /**
   * Set the component group name.
   * @param groupName The name of the component group.
   */
  public setComponentGroup(groupName: string): void {

    // Emit group changed.
    this.componentGroupService.set(this.componentGroupKey, groupName);
  }

  /**
   * On clear of component, set the form control value to null and run logic.
   */
  public onClearComponent(): void {
    this.componentFormControl.setValue(null);
    this.onSelectedComponent();
  }

  /**
   * On clear of component group, set the form control value to null.
   */
  public onClearComponentGroup(): void {
    this.componentGroupFormControl.setValue(null);

    // Emit group cleared.
    this.componentGroupService.set(this.componentGroupKey, '');
    this.componentGroupFormControl.enable();
    
    // Update cached properties after clearing component group
    this.updateCachedProperties();
  }

  public override validateForm(): void {
    super.validateForm();
    this.correctionEstimateList?.validateForm();

    // Check if we have basic customer concern filled out
    const hasBasicConcern = this.hasBasicCustomerConcern();
    
    if (this.correctionEstimateList) {
      // If we have basic concern, form is completed regardless of correction estimates or parent validation
      // Otherwise, use the original logic (parent form valid AND correction estimates completed)
      if (hasBasicConcern) {
        this.formCompleted = true;
      } else {
        this.formCompleted = this.formCompleted && this.correctionEstimateList.formCompleted;
      }
    } else {
      // If no correction estimate list, just use basic concern check
      if (hasBasicConcern) {
        this.formCompleted = true;
      }
    }
  }

  /**
   * Check if basic customer concern (component + cause + concern) is filled out
   */
  private hasBasicCustomerConcern(): boolean {
    const component = this.form.get('component')?.value;
    const cause = this.form.get('cause')?.value;
    const concern = this.form.get('concern')?.value;
    
    const hasValidComponent = component && typeof component === 'object' && component.name;
    const hasCause = cause && cause.toString().trim().length > 0;
    const hasConcern = concern && concern.toString().trim().length > 0;
    
    return hasValidComponent && hasCause && hasConcern;
  }

  /**
   * Clean up before removing this concern component.
   */
  public removing(): void {
    this.componentGroupService.delete(this.componentGroupKey);
  }
}
